import { PlusOutlined } from "@ant-design/icons";
import {
  useCreateBrandBilling,
  useGetBrandBillingFacets,
  useGetBrandsBilling
} from "@entities/billingBrand/lib/hooks";
import { Button, Modal, Row, Table, TableTitleLayout } from "@shared/ui";
import SelectFilter from "@shared/ui/filters/SelectFilter";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import { Col, Drawer, Flex, Input } from "antd";
import { useEffect, useState } from "react";

import { BrandBillingForm } from "@entities/billingBrand/ui/BrandBillingForm";
import { ManageBillingBrand } from "@features/ManageBillingBrand/ManageBillingBrand";
import { IBrandBilling } from "@shared/lib/types/brandBilling";
import { Search } from "lucide-react";
import styles from "./styles.module.scss";

export function BrandBillingManagement() {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedBilling, setSelectedBilling] = useState<string>("");
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);

  const { brandsBilling, isFetching } = useGetBrandsBilling(
    searchQuery,
    selectedBrands,
    pageSize,
    pageNo
  );

  const { create, isCreating, isCreated } = useCreateBrandBilling();

  const { facets } = useGetBrandBillingFacets();

  const linkedBrandFacetValues =
    facets?.brands?.map((brand) => ({
      name: brand.name,
      value: brand._id,
    })) || [];

  const handleCreateBrandBilling = () => {
    setSelectedBilling("");
    setModalVisible(true);
  };

  const handleSubmit = (brandBillingData: Partial<IBrandBilling>) => {
    create({ brandBilling: brandBillingData });
  };

  const handlePaginationChange = (page: number, size: number) => {
    setPageNo(page);
    setPageSize(size);
  };

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
  };

  const handleLinkedBrandsChange = (value: string[]) => {
    if (value === undefined) {
      setSelectedBrands([]);
    } else {
      setSelectedBrands(value);
    }
  };

  useEffect(() => {
    setPageNo(1);
  }, [pageSize, searchQuery]);

  useEffect(() => {
    if (isCreated) {
      setModalVisible(false);
    }
  }, [isCreated]);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (_: string, record: IBrandBilling) => (
        <Row
          align="middle"
          gutter={[8, 8]}
          style={{ cursor: "pointer" }}
          onClick={() => setSelectedBilling(record._id)}
        >
          <Col>
            <TextWithTooltip text={record.name} limit={30} />
          </Col>
        </Row>
      ),
    },
    {
      title: "GST",
      dataIndex: "gst",
      key: "gst",
      render: (gst: string) => <TextWithTooltip text={gst} limit={30} />,
    },
    {
      title: "GST Rate",
      dataIndex: "gstRate",
      key: "gstRate",
      render: (rate: number) => `${rate}%`,
    },
    {
      title: "Service Charge Rate",
      dataIndex: "serviceChargeRate",
      key: "serviceChargeRate",
      render: (rate: number) => `${rate}%`,
    },
    {
      title: "Linked Brands",
      dataIndex: "linkToBrands",
      key: "linkToBrands",
      filterDropdown: (props: { confirm: () => void; close: () => void }) => (
        <SelectFilter
          props={props}
          placeholder="Select Brand"
          initialTagCount={3}
          itemsToDisplay={linkedBrandFacetValues}
          selectedItems={selectedBrands}
          handleSelectionChange={handleLinkedBrandsChange}
        />
      ),
      render: (brands: any[]) => {
        const brandNames = brands?.map((brand) => brand.name).join(", ") || "";
        return (
          <Flex wrap vertical>
            {brands?.map((brand) => (
              <div>{brand.name}</div>
            ))}
          </Flex>
        );
      },
    },
  ];

  return (
    <div>
      <h1 className={styles.title}>Brand Billing</h1>

        <Table
          dataSource={brandsBilling?.data}
          columns={columns}
          title={() => <TableTitleLayout 
            filterComponent={<Flex justify="space-between" style={{width: "100%"}}>
              <Input
            placeholder="Search by billing name"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            allowClear
            style={{ width: 350 }}
            prefix={<Search size={14}/>}
          />
          <Button
            icon={<PlusOutlined />}
            type="primary"
            disabled={isCreating}
            onClick={() => handleCreateBrandBilling()}
          >
            Create Brand Billing
          </Button>
            </Flex>}
            totalText={`${brandsBilling?.pagination?.totalCount || 0} Records found`}
          />}
          loading={isFetching}
          rowKey="_id"
          pagination={{
            current: pageNo,
            pageSize: pageSize,
            total: brandsBilling?.pagination?.totalCount || 0,
            onChange: handlePaginationChange,
          }}
        />

      <Modal
        title={"Create Brand Billing"}
        open={isModalVisible}
        onCancel={() => {
          setModalVisible(false);
        }}
        footer={null}
        destroyOnClose
      >
        <BrandBillingForm onSubmit={handleSubmit} isSubmitting={isCreating} />
      </Modal>

      <Drawer
        destroyOnClose
        title={"Brand Billing"}
        open={Boolean(selectedBilling)}
        onClose={() => {
          setSelectedBilling("");
        }}
        footer={null}
        width={700}
      >
        {selectedBilling && <ManageBillingBrand billingId={selectedBilling} />}
      </Drawer>
    </div>
  );
}
